import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Search, Package, CheckCircle2, AlertTriangle } from 'lucide-react';

/**
 * Modal to map an unresolved transfer line to a destination-tenant product.
 * Opens from the Stock Transfers page when a line has `unresolved: true`.
 *
 * Props:
 *   open, onOpenChange, transferId, line, destTenantName, onMapped()
 */
export default function MapTransferProductDialog({
  open,
  onOpenChange,
  transferId,
  line,
  destTenantName,
  onMapped,
}) {
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Prefill search with the source product name so admins usually get the
  // right match on first keystroke.
  useEffect(() => {
    if (open && line?.product_name) {
      setQuery(line.product_name);
    }
    if (!open) {
      setCandidates([]);
    }
  }, [open, line]);

  const search = useCallback(async (q, signal) => {
    if (!transferId) return;
    setLoading(true);
    try {
      const res = await api.get(`/stock-transfers/${transferId}/candidates`, { params: { q, limit: 25 }, signal });
      setCandidates(res.data?.candidates || []);
    } catch (err) {
      if (err.code !== 'ERR_CANCELED') toast.error(err?.response?.data?.detail || 'Search failed');
    } finally { if (!signal.aborted) setLoading(false); }
  }, [transferId]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setLoading(true);
    const t = setTimeout(() => { search(query, controller.signal); }, 250);
    return () => { clearTimeout(t); controller.abort(); };
  }, [query, open, search]);

  const chooseCandidate = async (candidate) => {
    if (!line?.product_id) return;
    setSaving(true);
    try {
      await api.post(`/stock-transfers/${transferId}/map-product`, {
        source_product_id: line.product_id,
        dest_product_id: candidate.product_id,
      });
      toast.success(`Mapped to "${candidate.name}"`);
      onOpenChange(false);
      onMapped?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Mapping failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={value => { if (!saving) onOpenChange(value); }}>
      <DialogContent className="transfer-dialog" data-testid="map-product-dialog" closeProps={{ 'data-testid': 'map-product-close', disabled: saving }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-4 w-4 text-primary" /> Map unresolved item
          </DialogTitle>
          <DialogDescription>
            Pick the destination product this transfer line represents.
            {destTenantName && (
              <> Searching products in <b>{destTenantName}</b>.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {line && (
          <div className="rounded-lg border p-3 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800" data-testid="map-source-line">
            <div className="flex items-start gap-2 text-sm">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">{line.product_name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Source id <code>{line.product_id}</code> · quantity <b>{line.quantity}</b> · unit ₵ {Number(line.unit_cost || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="map-product-search">Search destination products</Label>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="map-product-search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Type a product name…"
              className="pl-9"
              autoFocus
              data-testid="map-product-search"
            />
          </div>
        </div>

        <div className="max-h-72 overflow-y-auto border rounded-md" data-testid="map-product-results">
          {loading ? (
            <div className="p-6 flex items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="map-product-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Searching…
            </div>
          ) : candidates.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="map-product-empty">
              No matches yet. Try a shorter search term (e.g. just the drug name), or add the product to the destination branch first.
            </div>
          ) : (
            <table className="w-full text-sm table-fixed break-words">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">Product</th>
                  <th className="p-2 text-left hidden sm:table-cell">Category</th>
                  <th className="p-2 text-right">Stock</th>
                  <th className="p-2 w-20" />
                </tr>
              </thead>
              <tbody>
                {candidates.map(c => (
                  <tr key={c.product_id} className="border-t hover:bg-muted/20">
                    <td className="p-2 break-all" data-testid={`map-product-name-${c.product_id}`}>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-[10px] text-muted-foreground">{c.product_id}</div>
                    </td>
                    <td className="p-2 text-xs hidden sm:table-cell" data-testid={`map-product-category-${c.product_id}`}>{c.category || '—'}</td>
                    <td className="p-2 text-right" data-testid={`map-product-stock-${c.product_id}`}>{c.stock_quantity ?? 0}</td>
                    <td className="p-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1"
                        onClick={() => chooseCandidate(c)}
                        disabled={saving}
                        data-testid={`map-product-choose-${c.product_id}`}
                      >
                        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                        Select
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <p className="text-[11px] text-muted-foreground">
          Mapping only updates this transfer line — future deliveries of the same SKU will still auto-resolve by name if the product names match exactly.
        </p>
      </DialogContent>
    </Dialog>
  );
}
